import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import FileUploader from '@/components/Admin/UI/FileUploader';
import IspLogoAvatar from './IspLogoAvatar';
import { toast } from 'react-toastify';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUIStore } from '@/store/useUIStore';
import { useIspGate } from './useIspGate';
import { PayLogo, PayLogoRow } from '@/components/Admin/PaymentLogos';
import {
   getIspTenant,
   getIspSmartfiBilling,
   saveIspTenant,
   saveIspPaymentMethods,
   type IspPaymentMethodInput,
   type IspPaymentMethod,
} from '@/api/isp';
import { IspPage } from './ispLayout';
import {
   SettingsCard,
   SettingsField,
   SettingsLbl,
   SettingsInput,
   SettingsHintSm,
   SettingsActions,
   SaveBtn,
   RowActions,
   LinkBtn,
   DangerLinkBtn,
} from './ispSettingsUI';
import {
   MOCK_PLANS,
   PAYMENT_KIND_LABEL,
   type IspPlan,
} from './ispMockData';
import type { PlanDraft } from './IspPlanForm';
import {
   type IspPlanRow,
   getIspPlans,
   createIspPlan,
   updateIspPlan,
   deleteIspPlan,
   pushIspPlanProfile,
   getIspServers,
} from '@/api/isp';
import type { PaymentMethodDraft } from './IspPaymentMethodForm';

/**
 * ISP Business — Settings, in three tabs.
 *
 * Ray, 2026-08-28: one long scroll of unrelated cards is not settings. Profile
 * is who the business is, Plans is what it sells, Payments is how it gets paid —
 * three different jobs an operator does at three different times.
 *
 * GRACE LIVES ON A PLAN, not here. A business package and a ₱799 home line do
 * not get the same patience, and one number for everybody means cutting off the
 * customer you least wanted to.
 */

const peso = (n: number) => '₱' + Number(n ?? 0).toLocaleString('en-PH');

const IspSetup: React.FC = () => {
   const { handleShow, handleClose } = useUIStore();
   const { gate } = useIspGate();

   // UI first, on mock data. Shaped like the server models so wiring the API
   // later changes where these come from and nothing else.
   const qc = useQueryClient();
   const { data: tenant } = useQuery({
      queryKey: ['isp-tenant'],
      queryFn: getIspTenant,
      staleTime: 60_000,
   });

   const [businessName, setBusinessName] = useState('');
   const [address, setAddress] = useState('');
   const [contactNumber, setContactNumber] = useState('');
   const [avatarUrl, setAvatarUrl] = useState('');
   const [saveError, setSaveError] = useState('');

   // Fill the fields in once what is saved arrives. Seeding straight from the
   // query would blank whatever the operator had already typed on every refetch.
   useEffect(() => {
      if (!tenant) return;
      setBusinessName((v) => v || tenant.businessName);
      setAddress((v) => v || tenant.address);
      setContactNumber((v) => v || tenant.contactNumber);
      setAvatarUrl((v) => v || (tenant.logoUrl ?? ''));
   }, [tenant]);

   const saveProfile = useMutation({
      mutationFn: saveIspTenant,
      onSuccess: () => {
         qc.invalidateQueries({ queryKey: ['isp-tenant'] });
         qc.invalidateQueries({ queryKey: ['isp-home'] });
         setSaveError('');
         toast.success('Saved.');
      },
      onError: (e: unknown) =>
         setSaveError(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not save. Try again.'
         ),
   });

   const submitProfile = () => {
      const name = businessName.trim();
      if (name.length < 2) {
         setSaveError('Give your ISP business a name.');
         return;
      }
      setSaveError('');
      saveProfile.mutate({
         businessName: name,
         logoUrl: avatarUrl,
         address: address.trim(),
         contactNumber: contactNumber.trim(),
         remindersEnabled: remindOn,
         remindBeforeDays: Number(remindDays) || 0,
      });
   };
   /**
    * Plans come from the server now, not MOCK_PLANS.
    *
    * A plan may have NO server on it — an operator writing their price list
    * before registering a MikroTik is a normal first session (Ray, 2026-08-31),
    * so the list says what is still missing rather than refusing to hold it.
    */
   const { data: plans = [] } = useQuery({
      queryKey: ['isp-plans'],
      queryFn: getIspPlans,
   });
   const { data: servers = [] } = useQuery({
      queryKey: ['isp-servers'],
      queryFn: getIspServers,
      staleTime: 30_000,
   });
   // Reminder emails are a Paid automation (A56) — say so where the switch is.
   const { data: billing } = useQuery({
      queryKey: ['isp-smartfi-billing'],
      queryFn: getIspSmartfiBilling,
      staleTime: 60_000,
   });
   const onFree = billing?.subscription === 'free';
   const serverNames = (ids: string[]) =>
      ids
         .map((id) => servers.find((sv) => sv.id === id)?.name)
         .filter(Boolean)
         .join(', ');

   const savePlan = useMutation({
      mutationFn: (v: { id: string | null; draft: PlanDraft }) =>
         v.id
            ? updateIspPlan(v.id, v.draft as never)
            : createIspPlan(v.draft as never),
      onSuccess: () => {
         qc.invalidateQueries({ queryKey: ['isp-plans'] });
         handleClose();
         toast.success('Saved.');
      },
      onError: (e: unknown) =>
         toast.error(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not save the plan.'
         ),
   });

   const removePlan = useMutation({
      mutationFn: (id: string) => deleteIspPlan(id),
      onSuccess: () => {
         qc.invalidateQueries({ queryKey: ['isp-plans'] });
         toast.success('Plan removed.');
      },
      // A 409 here means subscribers are still on it, and its message names how
      // many. Showing it verbatim is the whole point.
      onError: (e: unknown) =>
         toast.error(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not remove the plan.'
         ),
   });

   /**
    * Create this plan's /ppp/profile on its router.
    *
    * 🔴 The only write SmartFi makes to an operator's live PPPoE server, and it
    * happens only here, on a button that says so.
    */
   const pushProfile = useMutation({
      mutationFn: (id: string) => pushIspPlanProfile(id),
      onSuccess: (r) => toast.success(r.message),
      onError: (e: unknown) =>
         toast.error(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not write to the router.'
         ),
   });
   /**
    * Payment methods come from the SERVER now.
    *
    * 🔴 They were React state seeded from a mock until 2026-09-01, which meant
    * every edit an operator made survived exactly until they reloaded — the
    * same bug the customer list had. The QR they uploaded went with it.
    */
   const methods = tenant?.paymentMethods ?? [];

   /**
    * The public application page.
    *
    * Built from window.location so it is right on staging, on prod and on a
    * developer's laptop without a build-time constant that would be quietly
    * wrong on one of them.
    */
   const applyUrl = `${window.location.origin}/isp/${tenant?.portalSlug ?? ''}/apply`;

   /**
    * Emailing customers their bill.
    *
    * On by default (Ray, 2026-09-01) — an ISP that never tells anybody they owe
    * money chases every payment by hand. Only customers who gave an email get
    * one, which is most operators' minority; the portal and the printed bill
    * still work for everybody else.
    */
   const [remindersEnabled, setRemindersEnabled] = useState<boolean | null>(null);
   const [remindBefore, setRemindBefore] = useState<string>('');
   const remindOn = remindersEnabled ?? tenant?.remindersEnabled ?? true;
   const remindDays = remindBefore || String(tenant?.remindBeforeDays ?? 3);

   const saveMethods = useMutation({
      mutationFn: (next: IspPaymentMethodInput[]) => saveIspPaymentMethods(next),
      onSuccess: () => {
         qc.invalidateQueries({ queryKey: ['isp-tenant'] });
         toast.success('Payment methods saved.');
      },
      onError: (e: unknown) =>
         toast.error(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not save the payment methods.'
         ),
   });

   /** The list as the server needs it, with one row replaced or appended. */
   const withMethod = (
      existing: IspPaymentMethod | null,
      draft: PaymentMethodDraft
   ): IspPaymentMethodInput[] =>
      existing
         ? methods.map((m) => (m.id === existing.id ? { ...m, ...draft } : m))
         : [...methods, draft];

   const openPlan = (existing: IspPlanRow | null) =>
      handleShow({
         template: 'isp-plan-form',
         title: existing ? `Edit — ${existing.name}` : 'New plan',
         windowType: 'desktop',
         data: {
            existing,
            onCancel: handleClose,
            onSave: (draft: PlanDraft) =>
               savePlan.mutate({ id: existing?.id ?? null, draft }),
         },
      });

   const openMethod = (existing: IspPaymentMethod | null) =>
      handleShow({
         template: 'isp-payment-method-form',
         title: existing ? 'Edit payment method' : 'New payment method',
         windowType: 'desktop',
         data: {
            existing,
            onCancel: handleClose,
            onSave: (draft: PaymentMethodDraft) => {
               saveMethods.mutate(withMethod(existing, draft));
               handleClose();
            },
         },
      });

   return (
      <IspPage>
         <Title>Settings</Title>

         {/* ONE PAGE, NO TABS (Ray, 2026-10-30: "compact this design. no need
             separate tabs for payment… design similar like a distributors").
             Left: who the business is, then how customers pay it. Right: the
             reminder emails. One Save at the bottom. */}
         <Columns>
            <Col>
               <SettingsCard>
                  <ProfileTop>
                     {/* The logo sits inline with the name and number it
                         belongs to, instead of a lone circle under them. */}
                     <IspLogoAvatar
                        value={avatarUrl}
                        onChange={setAvatarUrl}
                        name={businessName}
                     />
                     <ProfileFields>
                        <SettingsField>
                           <SettingsLbl>Business name</SettingsLbl>
                           <SettingsInput
                              name="businessName"
                              value={businessName}
                              placeholder="e.g. Aztech Fiber"
                              onChange={(e) => setBusinessName(e.target.value)}
                           />
                        </SettingsField>
                        <SettingsField>
                           <SettingsLbl>Contact number</SettingsLbl>
                           <SettingsInput
                              name="contactNumber"
                              value={contactNumber}
                              placeholder="09XX XXX XXXX"
                              onChange={(e) => setContactNumber(e.target.value)}
                           />
                        </SettingsField>
                     </ProfileFields>
                  </ProfileTop>
                  <SettingsField>
                     <SettingsLbl>Address</SettingsLbl>
                     <SettingsInput
                        name="address"
                        value={address}
                        placeholder="Purok 3, Brgy. Lagao, General Santos City"
                        onChange={(e) => setAddress(e.target.value)}
                     />
                     <SettingsHintSm>
                        Your logo, name, number and address appear on every
                        invoice and the payment page.
                     </SettingsHintSm>
                  </SettingsField>
               </SettingsCard>

               {/* Has to read as PAYMENTS at a glance — the same card as the
                   distributor's "How buyers pay you". Empty = loud: a
                   suspended customer would have nowhere to send money. */}
               <PayCard $empty={methods.length === 0} data-testid="payment-methods">
                  <PayHead>
                     <div>
                        <PayTitle>💳 How customers pay you ({methods.length})</PayTitle>
                        <PaySub>
                           Shown to a customer on their bill and when they are
                           cut off, so they can pay with whichever they have.
                        </PaySub>
                     </div>
                     <PayLogoRow size={30} />
                  </PayHead>

                  {methods.length === 0 ? (
                     <PayEmpty role="alert">
                        <b>⚠️ Customers can't pay you yet</b>
                        <span>
                           Add GCash, Maya, a bank account or cash at your office.
                        </span>
                        <PayAddBig type="button" onClick={() => openMethod(null)}>
                           + Add payment method
                        </PayAddBig>
                     </PayEmpty>
                  ) : (
                     <>
                        <PayList>
                           {methods.map((m) => (
                              <PayRow key={m.id} $off={!m.active}>
                                 <MethodLogo kind={m.kind} />
                                 <PayInfo>
                                    <PayName>
                                       {PAYMENT_KIND_LABEL[m.kind]}
                                       {!m.active && <Off> · off</Off>}
                                    </PayName>
                                    {m.accountNumber && (
                                       <PayMono>{m.accountNumber}</PayMono>
                                    )}
                                    <PaySmall>
                                       {[m.accountName, m.instructions]
                                          .filter(Boolean)
                                          .join(' · ') || '—'}
                                    </PaySmall>
                                 </PayInfo>
                                 <RowActions>
                                    <LinkBtn type="button" onClick={() => openMethod(m)}>
                                       Edit
                                    </LinkBtn>
                                    {/* "Turn off", not "Delete" — payments point
                                        at a method, so the server keeps it. */}
                                    <DangerLinkBtn
                                       type="button"
                                       disabled={saveMethods.isPending}
                                       onClick={() =>
                                          saveMethods.mutate(
                                             methods.map((x) =>
                                                x.id === m.id
                                                   ? { ...x, active: !x.active }
                                                   : x
                                             )
                                          )
                                       }
                                    >
                                       {m.active ? 'Turn off' : 'Turn on'}
                                    </DangerLinkBtn>
                                 </RowActions>
                              </PayRow>
                           ))}
                        </PayList>
                        <PayAddSmall type="button" onClick={() => openMethod(null)}>
                           + Add another
                        </PayAddSmall>
                     </>
                  )}
               </PayCard>
            </Col>

            <Col>
               <SettingsCard>
                  <ToggleRow>
                     <Toggle
                        type="checkbox"
                        id="reminders-on"
                        checked={remindOn}
                        onChange={(e) => setRemindersEnabled(e.target.checked)}
                     />
                     <ToggleLbl htmlFor="reminders-on">
                        📧 Email customers about their bill
                     </ToggleLbl>
                  </ToggleRow>
                  <SettingsHintSm>
                     The bill when it is raised, a warning before it falls due,
                     one on the day and one after — each once. Only customers
                     with an email get them.
                  </SettingsHintSm>
                  {remindOn ? (
                     <InlineField>
                        <SettingsLbl htmlFor="remind-days">Warn them</SettingsLbl>
                        <DaysInput
                           id="remind-days"
                           value={remindDays}
                           inputMode="numeric"
                           onChange={(e) => setRemindBefore(e.target.value)}
                        />
                        <SettingsLbl as="span">days before the due date</SettingsLbl>
                     </InlineField>
                  ) : null}
                  {onFree && (
                     <FreeNote>
                        <strong>You are on Free:</strong> reminder emails only go
                        out on Paid. Upgrade in Subscriptions.
                     </FreeNote>
                  )}
               </SettingsCard>
            </Col>
         </Columns>

         {saveError && <Err>{saveError}</Err>}

         <SettingsActions>
            <SaveBtn
               type="button"
               disabled={saveProfile.isPending}
               onClick={submitProfile}
            >
               {saveProfile.isPending ? 'Saving…' : 'Save changes'}
            </SaveBtn>
         </SettingsActions>
      </IspPage>
   );
};


const ToggleRow = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   margin-bottom: 14px;
`;
const Toggle = styled.input`
   width: 18px;
   height: 18px;
`;
const ToggleLbl = styled.label`
   font-size: 14px;
   font-weight: 600;
`;

export default IspSetup;

const Title = styled.h1`
   margin: 0 0 4px;
   font-size: 20px;
`;

const Err = styled.p`
   margin: 12px 0 0;
   font-size: 12px;
   color: ${colors.DANGER};
`;


const Off = styled.span`
   color: ${colors.GRAY5};
   font-weight: 400;
`;

/**
 * Whether a plan has a router behind it.
 *
 * A plan with no MikroTik is a price and nothing more — it enforces no speed,
 * and an account cannot be put on it. That is invisible in a row that shows a
 * name and a peso figure, which is why it gets its own chip rather than a word
 * buried in the sub-line.
 */
const LinkChip = styled.span<{ $on: boolean }>`
   align-self: flex-start;
   margin-top: 4px;
   padding: 1px 8px;
   border-radius: 10px;
   font-size: 10.5px;
   font-weight: 700;
   white-space: nowrap;
   background: ${({ $on }) =>
      $on ? `${colors.SUCCESS}18` : `${colors.ORANGE}18`};
   color: ${({ $on }) => ($on ? colors.SUCCESS : colors.ORANGE)};
`;

/* ─── One-page layout (Ray, 2026-10-30) ───────────────────────── */

/** GCash / Maya / bank get their brand marks; cash and "other" a plain one. */
const MethodLogo: React.FC<{ kind: string }> = ({ kind }) =>
   kind === 'gcash' || kind === 'maya' || kind === 'bank' ? (
      <PayLogo kind={kind} size={34} />
   ) : (
      <PlainMark title={kind === 'cash' ? 'Cash' : 'Other'}>
         {kind === 'cash' ? '💵' : '💳'}
      </PlainMark>
   );

const Columns = styled.div`
   display: grid;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   gap: 16px;
   align-items: start;

   @media (max-width: 820px) {
      grid-template-columns: minmax(0, 1fr);
   }
`;

const Col = styled.div`
   display: flex;
   flex-direction: column;
   gap: 16px;
   min-width: 0;
`;

const ProfileTop = styled.div`
   display: grid;
   grid-template-columns: auto minmax(0, 1fr);
   gap: 16px;
   align-items: center;
`;

const ProfileFields = styled.div`
   display: grid;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   gap: 12px;

   @media (max-width: 560px) {
      grid-template-columns: minmax(0, 1fr);
   }
`;

const PayCard = styled.div<{ $empty: boolean }>`
   display: grid;
   gap: 12px;
   padding: 16px;
   border-radius: 8px;
   border: 1.5px solid ${({ $empty }) => ($empty ? '#f59e0b' : colors.GRAY12)};
   background: ${({ $empty }) =>
      $empty
         ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.10), rgba(245, 158, 11, 0.02))'
         : 'linear-gradient(135deg, rgba(55, 96, 249, 0.06), transparent)'};
`;

const PayHead = styled.div`
   display: flex;
   align-items: flex-start;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
`;

const PayTitle = styled.div`
   font-size: 14px;
   font-weight: 800;
   color: ${colors.MAIN_DARK};
`;

const PaySub = styled.div`
   margin-top: 2px;
   font-size: 12px;
   color: ${colors.GRAY5};
`;

const PayEmpty = styled.div`
   display: grid;
   gap: 6px;
   justify-items: start;

   b {
      font-size: 14px;
      color: #b45309;
   }
   span {
      font-size: 13px;
      color: ${colors.GRAY5};
   }
`;

const PayAddBig = styled.button`
   margin-top: 6px;
   padding: 10px 18px;
   border: 0;
   border-radius: 8px;
   background: ${colors.PRIMARY};
   color: #fff;
   font-weight: 800;
   font-size: 14px;
   cursor: pointer;
`;

const PayAddSmall = styled(PayAddBig)`
   justify-self: start;
   margin-top: 0;
   padding: 8px 14px;
   font-size: 12px;
`;

const PayList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

const PayRow = styled.div<{ $off?: boolean }>`
   display: grid;
   grid-template-columns: auto minmax(0, 1fr) auto;
   gap: 12px;
   align-items: center;
   padding: 10px 12px;
   border: 1px solid ${colors.GRAY13};
   border-radius: 8px;
   background: ${colors.WHITE};
   opacity: ${({ $off }) => ($off ? 0.55 : 1)};
`;

const PayInfo = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
   min-width: 0;
`;

const PayName = styled.span`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const PayMono = styled.span`
   font-family: monospace;
   font-size: 12px;
   color: ${colors.MAIN_DARK};
`;

const PaySmall = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const PlainMark = styled.span`
   display: inline-grid;
   place-items: center;
   width: 34px;
   height: 34px;
   border-radius: 10px;
   background: ${colors.GRAY13};
   font-size: 18px;
`;

const InlineField = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
`;

const DaysInput = styled(SettingsInput)`
   width: 64px;
   text-align: center;
`;

const FreeNote = styled.div`
   padding: 8px 10px;
   border-radius: 8px;
   background: #fff7e6;
   color: #8a5a00;
   font-size: 12px;
`;
